import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChevronLeft, ChevronRight, Clapperboard, ExternalLink, Search, ThumbsDown, ThumbsUp, Minus, X, Check, XCircle, Eye, Pencil, RefreshCw } from "lucide-react";
import {
  useAdminChallengeClipsQuery,
  useAdminSetChallengeClipApprovalMutation,
  useAdminSetChallengeClipHashtagValidationMutation,
  useAdminChallengeViewsTimeSeriesQuery,
} from "@/generated/graphql";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import { useMutation } from "@apollo/client";
import { ADMIN_CREATE_MANUAL_CLIP_VIEW, ADMIN_FORCE_UPDATE_ALL_CHALLENGE_CLIP_VIEWS, ADMIN_FORCE_UPDATE_CHALLENGE_CLIP_VIEW, ADMIN_SYNC_MISSING_CHALLENGE_CLIP_VIEWS } from "@/queries/queries";
import { formatDateTime, todayInTimezone } from "@/lib/format";

const CHALLENGE_TZ = "America/Sao_Paulo";
import { useToast } from "@/hooks/use-toast";

const APPROVAL_OPTIONS = ["neutral", "approved", "disapproved"] as const;
type Approval = typeof APPROVAL_OPTIONS[number];

const HASHTAG_OPTIONS = ["neutral", "validated", "invalidated"] as const;
type HashtagValidation = typeof HASHTAG_OPTIONS[number];


const AdminChallengeClips = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { challengeId } = useParams<{ challengeId: string }>();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [approvalFilter, setApprovalFilter] = useState<string>("");
  const [hashtagFilter, setHashtagFilter] = useState<string>("");
  const [page, setPage] = useState(1);
  const pageSize = 50;

  // Track which clip is pending disapproval (to show reason input)
  const [disapproveTarget, setDisapproveTarget] = useState<string | null>(null);
  const [disapproveReason, setDisapproveReason] = useState("");

  // Manual views entry
  const [manualViewsTarget, setManualViewsTarget] = useState<string | null>(null);
  const [manualViewsInput, setManualViewsInput] = useState("");

  const { data, loading, error, refetch } = useAdminChallengeClipsQuery({
    variables: {
      challengeId: challengeId!,
      page,
      pageSize,
      search: search || undefined,
      approval: approvalFilter || undefined,
      validated_hashtags: hashtagFilter || undefined,
    },
    skip: !challengeId,
    fetchPolicy: "cache-and-network",
  });

  const [setApproval, { loading: settingApproval }] = useAdminSetChallengeClipApprovalMutation();
  const [setHashtagValidation, { loading: settingHashtag }] = useAdminSetChallengeClipHashtagValidationMutation();
  const [createManualClipView, { loading: savingManualViews }] = useMutation(ADMIN_CREATE_MANUAL_CLIP_VIEW);
  const [forceUpdateAll, { loading: forcingUpdateAll }] = useMutation(ADMIN_FORCE_UPDATE_ALL_CHALLENGE_CLIP_VIEWS);
  const [syncMissing, { loading: syncingMissing }] = useMutation(ADMIN_SYNC_MISSING_CHALLENGE_CLIP_VIEWS);
  const [forceUpdateClip, { loading: forcingUpdateClip }] = useMutation(ADMIN_FORCE_UPDATE_CHALLENGE_CLIP_VIEW);

  const { data: timeSeriesData } = useAdminChallengeViewsTimeSeriesQuery({
    variables: { challengeId: challengeId!, days: 30 },
    skip: !challengeId,
    fetchPolicy: "cache-and-network",
  });

  const clips = data?.adminChallengeClips?.data || [];
  const totalPages = data?.adminChallengeClips?.pageCount || 1;
  const total = data?.adminChallengeClips?.total || 0;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleSetApproval = async (clipId: string, approval: Approval, reason?: string) => {
    try {
      await setApproval({ variables: { id: clipId, approval, reason: reason || undefined } });
      toast({ title: t("admin.challenges.approvalSet") });
      setDisapproveTarget(null);
      setDisapproveReason("");
      refetch();
    } catch (err: any) {
      toast({ title: err.message || t("admin.challenges.approvalError"), variant: "destructive" });
    }
  };

  const handleSetHashtagValidation = async (clipId: string, validated_hashtags: HashtagValidation) => {
    try {
      await setHashtagValidation({ variables: { id: clipId, validated_hashtags } });
      toast({ title: t("admin.challenges.hashtagValidationSet") });
      refetch();
    } catch (err: any) {
      toast({ title: err.message || t("admin.challenges.hashtagValidationError"), variant: "destructive" });
    }
  };

  const handleApprovalClick = (clipId: string, approval: Approval) => {
    if (approval === "disapproved") {
      setDisapproveTarget(clipId);
      setDisapproveReason("");
    } else {
      handleSetApproval(clipId, approval);
    }
  };

  const handleApprovalFilter = (val: string) => {
    setApprovalFilter(val);
    setPage(1);
  };

  const handleHashtagFilter = (val: string) => {
    setHashtagFilter(val);
    setPage(1);
  };

  const handleManualViews = async (clipId: string) => {
    const views = parseInt(manualViewsInput, 10);
    if (isNaN(views) || views < 0) {
      toast({ title: t("admin.challenges.manualViewsInvalid"), variant: "destructive" });
      return;
    }
    try {
      await createManualClipView({ variables: { clipId, views: String(views) } });
      toast({ title: t("admin.challenges.manualViewsSaved") });
      setManualViewsTarget(null);
      setManualViewsInput("");
      refetch();
    } catch (err: any) {
      toast({ title: err.message || t("admin.challenges.manualViewsError"), variant: "destructive" });
    }
  };

  const handleForceUpdateAll = async () => {
    try {
      const result = await forceUpdateAll({ variables: { challengeId: challengeId! } });
      const count = result.data?.adminForceUpdateAllChallengeClipViews || 0;
      toast({ title: t("admin.challenges.forceUpdateAllViewsSuccess", { count }) });
    } catch (err: any) {
      toast({ title: err.message || t("admin.challenges.forceUpdateAllViewsError"), variant: "destructive" });
    }
  };

  const handleSyncMissing = async () => {
    try {
      const result = await syncMissing({ variables: { challengeId: challengeId! } });
      const count = result.data?.adminSyncMissingChallengeClipViews || 0;
      toast({
        title: count > 0
          ? t("admin.challenges.syncMissingViewsSuccess", { count })
          : t("admin.challenges.syncMissingViewsNone"),
      });
    } catch (err: any) {
      toast({ title: err.message || t("admin.challenges.syncMissingViewsError"), variant: "destructive" });
    }
  };

  const handleForceUpdateClip = async (clipId: string) => {
    try {
      await forceUpdateClip({ variables: { clipId } });
      toast({ title: t("admin.challenges.forceUpdateClipViewSuccess") });
    } catch (err: any) {
      toast({ title: err.message || t("admin.challenges.forceUpdateClipViewError"), variant: "destructive" });
    }
  };

  const colSpan = 7;

  return (
    <Layout>
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate("/admin/challenges")}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                {t("common.back")}
              </Button>
              <div className="flex items-center gap-2">
                <Clapperboard className="w-6 h-6 text-primary" />
                <div>
                  <h1 className="text-xl font-bold">{t("admin.challenges.viewClips")}</h1>
                  <p className="text-sm text-muted-foreground">{total} {t("admin.challenges.clipCount").toLowerCase()}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={syncingMissing}
                  >
                    <RefreshCw className={`w-4 h-4 mr-1 ${syncingMissing ? "animate-spin" : ""}`} />
                    {t("admin.challenges.syncMissingViews")}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("admin.challenges.syncMissingViews")}</AlertDialogTitle>
                    <AlertDialogDescription>{t("admin.challenges.syncMissingViewsConfirm")}</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                    <AlertDialogAction onClick={handleSyncMissing}>{t("common.confirm")}</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={forcingUpdateAll}
                  >
                    <RefreshCw className={`w-4 h-4 mr-1 ${forcingUpdateAll ? "animate-spin" : ""}`} />
                    {t("admin.challenges.forceUpdateAllViews")}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("admin.challenges.forceUpdateAllViews")}</AlertDialogTitle>
                    <AlertDialogDescription>{t("admin.challenges.forceUpdateAllViewsConfirm")}</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                    <AlertDialogAction onClick={handleForceUpdateAll}>{t("common.confirm")}</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <Badge variant="outline" className="text-red-500 border-red-500">ADMIN</Badge>
            </div>
          </div>

          {/* Summary Cards */}
          {timeSeriesData?.adminChallengeViewsTimeSeries && timeSeriesData.adminChallengeViewsTimeSeries.length > 0 && (() => {
            const series = timeSeriesData.adminChallengeViewsTimeSeries;
            const latestViews = parseInt(series[series.length - 1]?.total_views || "0", 10);
            const totalClipsInPeriod = series.reduce((sum, d) => sum + (d.clip_count || 0), 0);
            const avgViewsPerClip = totalClipsInPeriod > 0 ? Math.round(latestViews / totalClipsInPeriod) : 0;
            const today = todayInTimezone(CHALLENGE_TZ);
            const todayEntry = series.find((d) => d.date === today);
            const newClipsToday = todayEntry?.clip_count || 0;

            return (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4 pb-3 px-4">
                    <p className="text-xs text-muted-foreground">{t("admin.challenges.summaryTotalViews")}</p>
                    <p className="text-2xl font-bold">{latestViews >= 1000000 ? `${(latestViews / 1000000).toFixed(1)}M` : latestViews >= 1000 ? `${(latestViews / 1000).toFixed(1)}K` : latestViews.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-3 px-4">
                    <p className="text-xs text-muted-foreground">{t("admin.challenges.summaryTotalClips")}</p>
                    <p className="text-2xl font-bold">{totalClipsInPeriod.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-3 px-4">
                    <p className="text-xs text-muted-foreground">{t("admin.challenges.summaryAvgViewsPerClip")}</p>
                    <p className="text-2xl font-bold">{avgViewsPerClip >= 1000 ? `${(avgViewsPerClip / 1000).toFixed(1)}K` : avgViewsPerClip.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-3 px-4">
                    <p className="text-xs text-muted-foreground">{t("admin.challenges.summaryNewClipsToday")}</p>
                    <p className="text-2xl font-bold">{newClipsToday}</p>
                  </CardContent>
                </Card>
              </div>
            );
          })()}

          {/* Views Growth & Clips Chart */}
          {timeSeriesData?.adminChallengeViewsTimeSeries && timeSeriesData.adminChallengeViewsTimeSeries.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t("admin.challenges.viewsGrowthChart")}</CardTitle>
              </CardHeader>
              <CardContent>
                <ChartContainer config={{
                  total_views: { label: t("admin.challenges.totalViews"), color: "hsl(var(--primary))" },
                  clip_count: { label: t("admin.challenges.newClips"), color: "hsl(var(--chart-2))" },
                } satisfies ChartConfig} className="h-[280px] w-full">
                  <ComposedChart data={timeSeriesData.adminChallengeViewsTimeSeries.map((d) => ({ date: d.date, total_views: parseInt(d.total_views || "0", 10), clip_count: d.clip_count || 0 }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} allowDecimals={false} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar yAxisId="left" dataKey="total_views" fill="var(--color-total_views)" radius={[4, 4, 0, 0]} />
                    <Line yAxisId="right" type="linear" dataKey="clip_count" stroke="#4ade80" strokeWidth={2.5} dot={{ r: 4, fill: "#4ade80", strokeWidth: 0 }} connectNulls />
                  </ComposedChart>
                </ChartContainer>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <CardTitle>{t("admin.challenges.viewClips")}</CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Hashtag validation filter buttons */}
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-muted-foreground">{t("admin.challenges.hashtagValidation")}:</span>
                    <div className="flex items-center gap-1 border border-border rounded-md p-1">
                      {["", ...HASHTAG_OPTIONS].map((val) => (
                        <Button
                          key={`h-${val}`}
                          variant={hashtagFilter === val ? "default" : "ghost"}
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => handleHashtagFilter(val)}
                        >
                          {val === "" ? t("admin.challenges.approvalAll")
                            : val === "neutral" ? t("admin.challenges.hashtagNeutral")
                            : val === "validated" ? t("admin.challenges.hashtagValidated")
                            : t("admin.challenges.hashtagInvalidated")}
                        </Button>
                      ))}
                    </div>
                  </div>
                  {/* Approval filter buttons */}
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-muted-foreground">{t("admin.challenges.approval")}:</span>
                    <div className="flex items-center gap-1 border border-border rounded-md p-1">
                      {["", ...APPROVAL_OPTIONS].map((val) => (
                        <Button
                          key={val}
                          variant={approvalFilter === val ? "default" : "ghost"}
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => handleApprovalFilter(val)}
                        >
                          {val === "" ? t("admin.challenges.approvalAll")
                            : val === "neutral" ? t("admin.challenges.approvalNeutral")
                            : val === "approved" ? t("admin.challenges.approvalApprove")
                            : t("admin.challenges.approvalDisapprove")}
                        </Button>
                      ))}
                    </div>
                  </div>
                  {/* Search */}
                  <form onSubmit={handleSearch} className="flex gap-1">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        placeholder={t("admin.challenges.searchPlaceholder")}
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        className="pl-8 max-w-xs h-9"
                      />
                    </div>
                    <Button type="submit" variant="outline" size="sm">
                      <Search className="w-4 h-4" />
                    </Button>
                  </form>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading && !data ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </div>
              ) : error ? (
                <div className="text-center py-8 text-red-500">{t("admin.common.error")}</div>
              ) : (
                <>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Platform</TableHead>
                          <TableHead>URL</TableHead>
                          <TableHead>{t("admin.challenges.participant")}</TableHead>
                          <TableHead>{t("admin.challenges.totalViews")}</TableHead>
                          <TableHead>{t("admin.challenges.hashtagValidation")}</TableHead>
                          <TableHead>{t("admin.challenges.createdAt")}</TableHead>
                          <TableHead className="w-[160px]">{t("admin.challenges.actions")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {clips.map((clip) => (
                          <>
                            <TableRow key={clip.id} className={clip.admin_approval === "disapproved" ? "opacity-60" : ""}>
                              <TableCell>
                                <span className="text-xs font-semibold uppercase px-2 py-1 rounded bg-primary/10 text-primary">
                                  {clip.platform}
                                </span>
                              </TableCell>
                              <TableCell className="max-w-xs">
                                <a
                                  href={clip.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 text-sm text-blue-400 hover:underline truncate"
                                >
                                  <ExternalLink className="w-3 h-3 shrink-0" />
                                  <span className="truncate">{clip.url}</span>
                                </a>
                                {clip.admin_approval === "disapproved" && clip.approval_reason && (
                                  <p className="text-xs text-red-400 mt-0.5">
                                    {t("admin.challenges.approvalReason")}: {clip.approval_reason}
                                  </p>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="text-sm">
                                  <p className="font-medium">{clip.participant_user_name || "—"}</p>
                                  <p className="text-xs text-muted-foreground">{clip.participant_user_email || "—"}</p>
                                </div>
                              </TableCell>
                              <TableCell>{parseInt(clip.total_views || "0", 10).toLocaleString()}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-0.5">
                                    <Button
                                      variant={clip.validated_hashtags === "validated" ? "default" : "ghost"}
                                      size="sm"
                                      className={`h-6 w-6 p-0 ${clip.validated_hashtags === "validated" ? "text-gray-900" : "text-emerald-500"}`}
                                      onClick={() => handleSetHashtagValidation(clip.id, "validated")}
                                      disabled={settingHashtag}
                                      title={t("admin.challenges.hashtagValidated")}
                                    >
                                      <Check className="w-3 h-3" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className={`h-6 w-6 p-0 ${clip.validated_hashtags === "neutral" ? "bg-gray-500/20 text-gray-300" : "text-muted-foreground"}`}
                                      onClick={() => handleSetHashtagValidation(clip.id, "neutral")}
                                      disabled={settingHashtag}
                                      title={t("admin.challenges.hashtagNeutral")}
                                    >
                                      <Minus className="w-3 h-3" />
                                    </Button>
                                    <Button
                                      variant={clip.validated_hashtags === "invalidated" ? "destructive" : "ghost"}
                                      size="sm"
                                      className={`h-6 w-6 p-0 ${clip.validated_hashtags === "invalidated" ? "" : "text-red-500"}`}
                                      onClick={() => handleSetHashtagValidation(clip.id, "invalidated")}
                                      disabled={settingHashtag}
                                      title={t("admin.challenges.hashtagInvalidated")}
                                    >
                                      <XCircle className="w-3 h-3" />
                                    </Button>
                                </div>
                              </TableCell>
                              <TableCell>{formatDateTime(clip.createdAt, CHALLENGE_TZ)}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-0.5">
                                  <Button
                                    variant={clip.admin_approval === "approved" ? "default" : "ghost"}
                                    size="sm"
                                    className={clip.admin_approval === "approved" ? "text-gray-900" : "text-emerald-500"}
                                    onClick={() => handleApprovalClick(clip.id, "approved")}
                                    disabled={settingApproval}
                                    title={t("admin.challenges.approvalApprove")}
                                  >
                                    <ThumbsUp className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className={clip.admin_approval === "neutral" ? "bg-gray-500/20 text-gray-300" : "text-muted-foreground"}
                                    onClick={() => handleApprovalClick(clip.id, "neutral")}
                                    disabled={settingApproval}
                                    title={t("admin.challenges.approvalNeutral")}
                                  >
                                    <Minus className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant={clip.admin_approval === "disapproved" ? "destructive" : "ghost"}
                                    size="sm"
                                    className={clip.admin_approval === "disapproved" ? "" : "text-red-500"}
                                    onClick={() => handleApprovalClick(clip.id, "disapproved")}
                                    disabled={settingApproval}
                                    title={t("admin.challenges.approvalDisapprove")}
                                  >
                                    <ThumbsDown className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-gray-300"
                                    onClick={() => { setManualViewsTarget(clip.id); setManualViewsInput(""); }}
                                    title={t("admin.challenges.manualViews")}
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-blue-400"
                                    onClick={() => handleForceUpdateClip(clip.id)}
                                    disabled={forcingUpdateClip}
                                    title={t("admin.challenges.forceUpdateClipView")}
                                  >
                                    <RefreshCw className={`w-4 h-4 ${forcingUpdateClip ? "animate-spin" : ""}`} />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                            {disapproveTarget === clip.id && (
                              <TableRow key={`${clip.id}-reason`}>
                                <TableCell colSpan={colSpan}>
                                  <div className="border border-red-500/30 rounded-lg p-3 space-y-2 bg-red-500/5">
                                    <p className="text-xs font-medium text-red-400">{t("admin.challenges.disapproveConfirm")}</p>
                                    <div className="space-y-1">
                                      <Label className="text-xs">{t("admin.challenges.approvalReason")}</Label>
                                      <Textarea
                                        value={disapproveReason}
                                        onChange={(e) => setDisapproveReason(e.target.value)}
                                        placeholder={t("admin.challenges.approvalReasonPlaceholder")}
                                        rows={2}
                                      />
                                    </div>
                                    <div className="flex gap-2">
                                      <Button
                                        variant="destructive"
                                        size="sm"
                                        onClick={() => handleSetApproval(clip.id, "disapproved", disapproveReason)}
                                        disabled={settingApproval}
                                      >
                                        <ThumbsDown className="w-3 h-3 mr-1" />
                                        {t("admin.challenges.approvalDisapprove")}
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => { setDisapproveTarget(null); setDisapproveReason(""); }}
                                      >
                                        <X className="w-3 h-3 mr-1" />
                                        {t("common.cancel")}
                                      </Button>
                                    </div>
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                            {manualViewsTarget === clip.id && (
                              <TableRow key={`${clip.id}-manual-views`}>
                                <TableCell colSpan={colSpan}>
                                  <div className="border border-blue-500/30 rounded-lg p-3 space-y-2 bg-blue-500/5">
                                    <p className="text-xs font-medium text-blue-400">{t("admin.challenges.manualViewsConfirm")}</p>
                                    <div className="flex items-center gap-2">
                                      <Label className="text-xs shrink-0">{t("admin.challenges.manualViewsLabel")}</Label>
                                      <Input
                                        type="number"
                                        min={0}
                                        value={manualViewsInput}
                                        onChange={(e) => setManualViewsInput(e.target.value)}
                                        placeholder="0"
                                        className="h-8 w-40"
                                        onKeyDown={(e) => e.key === "Enter" && handleManualViews(clip.id)}
                                      />
                                    </div>
                                    <div className="flex gap-2">
                                      <Button
                                        variant="default"
                                        size="sm"
                                        className="text-gray-900"
                                        onClick={() => handleManualViews(clip.id)}
                                        disabled={savingManualViews}
                                      >
                                        <Eye className="w-3 h-3 mr-1" />
                                        {t("admin.challenges.manualViewsSave")}
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => { setManualViewsTarget(null); setManualViewsInput(""); }}
                                      >
                                        <X className="w-3 h-3 mr-1" />
                                        {t("common.cancel")}
                                      </Button>
                                    </div>
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        ))}
                        {clips.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={colSpan} className="text-center py-10 text-muted-foreground">
                              {t("admin.common.noResults")}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="flex items-center justify-between mt-4">
                    <span className="text-sm text-muted-foreground">
                      {t("admin.common.page")} {page} {t("admin.common.of")} {totalPages}
                    </span>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </Layout>
  );
};

export default AdminChallengeClips;
